import type { ISupplierID } from '@bridebook/models/source/models/Suppliers.types';
import Hashids from 'hashids';
import type { CountryCodes } from '../gazetteer';
import { isSupplierId } from '../is';

export type DecodedSupplierId = {
  numbers: (bigint | number)[];
  realId: boolean;
  supplierId: string;
};

/**
 * Default hashids instance, with pre-defined salt, length and alphabet.
 */
const hashids = new Hashids(
  'big brown fox',
  8,
  'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890',
);

/**
 * Uses Crawford's charset to avoid confusion between casing, similar characters and curse words in the generated IDs.
 */
const crawford = new Hashids('afs973x5235gf8fh', 7, 'abcdefghjkmnpqrstvwxyz0123456789');

/**
 * Encodes a supplier ID using Crawford's case-insensitive charset.
 *
 * @param value A supplier ID such as `BB123456` or `DE12345678`.
 */
export function crawfordEncode(value: ISupplierID): string {
  if (isSupplierId(value) !== true) {
    throw new Error(`Invalid supplier ID: ${value}`);
  }

  return `${value.substring(0, 2).toLowerCase()}${crawford.encode(+value.substring(2))}`;
}

/**
 * Decodes a public supplier ID encoded with `crawfordEncode()` and returns the original supplier ID.
 *
 * @param value A public supplier ID such as `bbrxj10kr` or `dex43z912`.
 */
export function crawfordDecode(value: string): ISupplierID {
  let result: bigint | number | undefined = undefined;

  try {
    result = crawford.decode(value.substring(2).toLowerCase()).at(0);

    if (result == null) {
      throw new Error();
    }
  } catch (error) {
    throw new Error(`Invalid encoded supplier ID: ${value}`);
  }

  /**
   * BB and EE prefixes are historically 6 digits long, this is the only exception to the rule.
   */
  const prefix = value.substring(0, 2).toUpperCase();
  const suffix = result.toString().padStart(['BB', 'EE'].includes(prefix) ? 6 : 8, '0');

  if (isSupplierId(`${prefix}${suffix}`) !== true) {
    throw new Error(`Decoded supplier ID is invalid: ${prefix}${suffix}`);
  }

  return `${prefix}${suffix}`;
}

/**
 * Returns a decoded supplier ID struct.
 *
 * @param id A public supplier ID such as `cs12345678`, optionally prepended with location slugs.
 */
export function extractPublicId(id: string): string {
  return id.split('-').pop() as string;
}

export function decodePublicSupplierId(id: string): DecodedSupplierId {
  /**
   * We split by `-` and take the last part of the string, which is the public ID.
   * We do this because we might want to pass a location-concatenated public ID?
   */
  const publicId = extractPublicId(id);
  const numbers = hashids.decode(publicId.substring(2));

  /**
   * BB and EE prefixes are historically 6 digits long, this is the only exception to the rule.
   */
  const prefix = publicId.substring(0, 2).toUpperCase();
  const suffix = numbers.toString().padStart(['BB', 'EE'].includes(prefix) ? 6 : 8, '0');

  return {
    numbers,
    realId: false,
    supplierId: `${prefix}${suffix}`,
  };
}

/**
 * Returns a supplier public ID.
 *
 * @param id A supplier ID such as `BB123456` or `DE12345678`.
 * @param prefix The prefix to use for the public ID, defaults to `cs`.
 */
export function encodePublicSupplierId(id: ISupplierID, prefix?: CountryCodes | 'CS') {
  return `${(prefix && prefix?.toLowerCase()) || 'cs'}${hashids.encode(+id.substring(2))}`;
}
